import {
  BISHOP,
  BLACK,
  BLACK_BIT,
  CASTLE_BK,
  CASTLE_BQ,
  CASTLE_MASK,
  CASTLE_WK,
  CASTLE_WQ,
  type Color,
  COLOR_MASK,
  FLAG_ACTION,
  FLAG_CAPTURE,
  FLAG_CASTLE,
  FLAG_DOUBLE,
  FLAG_DROP,
  FLAG_EP,
  KING,
  KNIGHT,
  PAWN,
  QUEEN,
  ROOK,
  TYPE_MASK,
  WHITE,
  WHITE_BIT,
  colorBit,
  encodeMove,
  isLightSquare,
  makePiece,
  moveFlags,
  moveFrom,
  movePromo,
  moveTo,
  onBoard,
  other,
  pieceColor,
} from './board.ts';
import { type PieceDef, PIECE_DEFS, type Vec, baseType } from './pieces.ts';
import type { GameStatus, MoveEvent, Position, Variant } from './types.ts';

export function resultFor(winner: Color | null) {
  return winner === null ? ('1/2-1/2' as const) : winner === WHITE ? ('1-0' as const) : ('0-1' as const);
}

export function win(winner: Color, reason: string): GameStatus {
  return { over: true, result: resultFor(winner), reason, winner };
}

export function draw(reason: string): GameStatus {
  return { over: true, result: '1/2-1/2', reason };
}

const ONGOING: GameStatus = Object.freeze({ over: false });

/**
 * Passed to variant hooks while a move is being made. `next` is the position being built;
 * use the helpers to change the board so royals, castling rights, the 50-move counter
 * and UI events stay consistent.
 */
export class MoveContext {
  constructor(
    readonly rules: Rules,
    readonly prev: Position,
    readonly next: Position,
    readonly move: number,
    readonly us: Color,
    readonly them: Color,
    /** The piece that moved (or was dropped), before promotion. */
    readonly piece: number,
    /** Piece taken by the move itself, 0 if none. */
    readonly captured: number,
    readonly capturedSq: number,
    /** Collected only when the caller asked for events (UI); null inside the AI. */
    readonly events: MoveEvent[] | null,
  ) {}

  /** Removes whatever stands on `sq`. Returns the piece removed (0 if the square was empty). */
  remove(sq: number, cause = 'removed'): number {
    const next = this.next;
    const p = next.board[sq];
    if (!p) return 0;
    next.board[sq] = 0;
    next.halfmove = 0;
    next.castling &= CASTLE_MASK[sq];
    const c = pieceColor(p);
    if (c !== -1 && next.royals[c] === sq) next.royals[c] = -1;
    this.events?.push({ t: 'remove', sq, piece: p, cause });
    return p;
  }

  /** Moves the piece on `from` to `to`, destroying anything on `to`. Returns the piece destroyed. */
  relocate(from: number, to: number, cause = 'removed'): number {
    const next = this.next;
    const p = next.board[from];
    if (!p || from === to) return 0;
    this.events?.push({ t: 'move', from, to, piece: p });
    const victim = this.remove(to, cause);
    next.board[from] = 0;
    next.board[to] = p;
    next.castling &= CASTLE_MASK[from];
    const c = pieceColor(p);
    if (c !== -1 && next.royals[c] === from) next.royals[c] = to;
    return victim;
  }

  /** Puts `piece` on `sq`, removing anything already there. */
  place(sq: number, piece: number, cause = 'spawn') {
    if (this.next.board[sq]) this.remove(sq, cause);
    this.next.board[sq] = piece;
    const c = pieceColor(piece);
    if (c !== -1 && this.rules.isRoyal[piece & TYPE_MASK]) this.next.royals[c] = sq;
    this.events?.push({ t: 'add', sq, piece, cause });
  }

  fx(kind: string, sq: number) {
    this.events?.push({ t: 'fx', kind, sq });
  }
}

interface AttackLeap {
  mask: number;
  /** src[sq] = square an attacker would leap from to hit sq, or -1. */
  src: Int8Array;
}

interface AttackSlide {
  mask: number;
  /** rev[sq] = squares walking from sq back towards a potential slider. */
  rev: number[][];
}

const mirror = (v: Vec, c: Color): Vec => (c === WHITE ? v : [v[0], -v[1]]);

function leapTargets(vecs: readonly Vec[] | undefined, c: Color): number[][] {
  const out: number[][] = [];
  for (let sq = 0; sq < 64; sq++) {
    out[sq] = [];
    if (!vecs) continue;
    for (const v of vecs) {
      const [df, dr] = mirror(v, c);
      const f = (sq & 7) + df;
      const r = (sq >> 3) + dr;
      if (onBoard(f, r)) out[sq].push(r * 8 + f);
    }
  }
  return out;
}

function ray(sq: number, df: number, dr: number): number[] {
  const out: number[] = [];
  let f = (sq & 7) + df;
  let r = (sq >> 3) + dr;
  while (onBoard(f, r)) {
    out.push(r * 8 + f);
    f += df;
    r += dr;
  }
  return out;
}

const DEFAULT_PROMOTIONS = [QUEEN, ROOK, BISHOP, KNIGHT];

/** The compiled rules of one variant. Get it with `rulesFor(variant)`. */
export class Rules {
  readonly variant: Variant;
  /** Piece types that can appear (standard six plus the variant's extras). */
  readonly types: number[];
  readonly defs: (PieceDef | undefined)[] = [];
  readonly isRoyal: boolean[] = [];
  readonly isPawn: boolean[] = [];
  readonly hasRoyal: boolean;
  readonly checks: boolean;
  readonly royalKillWins: boolean;
  readonly castling: boolean;
  readonly doublePawnStep: boolean;
  readonly enPassant: boolean;
  readonly drops: boolean;
  readonly captureToHand: boolean;
  readonly promotions: number[];
  readonly extSize: number;

  // [color][type][square] → targets
  private readonly leapTo: number[][][][] = [[], []];
  private readonly leapMoveTo: number[][][][] = [[], []];
  private readonly leapCapTo: number[][][][] = [[], []];
  private readonly rays: number[][][][][] = [[], []];
  private readonly pawnCapTo: number[][][][] = [[], []];
  // [attacker color]
  private readonly attackLeaps: AttackLeap[][] = [[], []];
  private readonly attackSlides: AttackSlide[][] = [[], []];

  constructor(variant: Variant) {
    this.variant = variant;
    this.types = [PAWN, KNIGHT, BISHOP, ROOK, QUEEN, KING, ...(variant.extraPieces ?? [])];
    this.checks = variant.checks ?? true;
    this.royalKillWins = variant.royalKillWins ?? false;
    this.castling = variant.castling ?? true;
    this.doublePawnStep = variant.doublePawnStep ?? true;
    this.enPassant = variant.enPassant ?? true;
    this.drops = variant.drops ?? false;
    this.captureToHand = variant.captureToHand ?? false;
    this.promotions = variant.promotions ?? DEFAULT_PROMOTIONS;
    this.extSize = variant.extSize ?? 0;

    for (const t of this.types) {
      const def = PIECE_DEFS[t];
      if (!def) throw new Error(`Variant ${variant.id} uses unknown piece type ${t}`);
      this.defs[t] = def;
      this.isRoyal[t] = !!def.royal;
      this.isPawn[t] = !!def.pawn;
    }
    this.hasRoyal = this.types.some((t) => this.isRoyal[t]);

    for (const c of [WHITE, BLACK] as Color[]) {
      const leapMap = new Map<string, AttackLeap>();
      const slideMap = new Map<string, AttackSlide>();
      for (const t of this.types) {
        const def = this.defs[t]!;
        if (def.neutral) continue;
        if (def.pawn) {
          this.pawnCapTo[c][t] = leapTargets(def.leapsCapture, c);
        } else {
          this.leapTo[c][t] = leapTargets(def.leaps, c);
          this.leapMoveTo[c][t] = leapTargets(def.leapsMove, c);
          this.leapCapTo[c][t] = leapTargets(def.leapsCapture, c);
        }
        const slides = def.slides ?? [];
        this.rays[c][t] = [];
        for (let sq = 0; sq < 64; sq++) {
          this.rays[c][t][sq] = slides.map((v) => {
            const [df, dr] = mirror(v, c);
            return ray(sq, df, dr);
          });
        }
        // Attack tables, seen from the attacked square.
        for (const v of [...(def.leaps ?? []), ...(def.leapsCapture ?? [])]) {
          const [df, dr] = mirror(v, c);
          const key = `${df},${dr}`;
          let entry = leapMap.get(key);
          if (!entry) {
            const src = new Int8Array(64).fill(-1);
            for (let sq = 0; sq < 64; sq++) {
              const f = (sq & 7) - df;
              const r = (sq >> 3) - dr;
              if (onBoard(f, r)) src[sq] = r * 8 + f;
            }
            entry = { mask: 0, src };
            leapMap.set(key, entry);
          }
          entry.mask |= 1 << t;
        }
        for (const v of slides) {
          const [df, dr] = mirror(v, c);
          const key = `${df},${dr}`;
          let entry = slideMap.get(key);
          if (!entry) {
            const rev: number[][] = [];
            for (let sq = 0; sq < 64; sq++) rev[sq] = ray(sq, -df, -dr);
            entry = { mask: 0, rev };
            slideMap.set(key, entry);
          }
          entry.mask |= 1 << t;
        }
      }
      this.attackLeaps[c] = [...leapMap.values()];
      this.attackSlides[c] = [...slideMap.values()];
    }
  }

  // -------------------------------------------------------------------------
  // Attacks & status

  /** True if a piece of colour `by` attacks `sq` (ignores pins and variant legality). */
  isAttacked(board: Int8Array, sq: number, by: Color): boolean {
    const bit = colorBit(by);
    const leaps = this.attackLeaps[by];
    for (let i = 0; i < leaps.length; i++) {
      const s = leaps[i].src[sq];
      if (s < 0) continue;
      const p = board[s];
      if (p & bit && leaps[i].mask & (1 << (p & TYPE_MASK))) return true;
    }
    const slides = this.attackSlides[by];
    for (let i = 0; i < slides.length; i++) {
      const rev = slides[i].rev[sq];
      for (let j = 0; j < rev.length; j++) {
        const p = board[rev[j]];
        if (p === 0) continue;
        if (p & bit && slides[i].mask & (1 << (p & TYPE_MASK))) return true;
        break;
      }
    }
    return !!this.variant.attacks && this.variant.attacks(board, sq, by);
  }

  inCheck(pos: Position): boolean {
    if (this.variant.inCheck) return this.variant.inCheck(pos);
    const k = pos.royals[pos.turn];
    return k >= 0 && this.isAttacked(pos.board, k, other(pos.turn));
  }

  /** In check, or facing a variant threat the side to move must answer. */
  isThreatened(pos: Position): boolean {
    return this.inCheck(pos) || (!!this.variant.threatened && this.variant.threatened(pos));
  }

  // -------------------------------------------------------------------------
  // Move generation

  /**
   * Pseudo-legal moves: they may leave the royal attacked or break a variant rule.
   * With `capturesOnly`, quiet moves are skipped but promotions are kept.
   */
  pseudoMoves(pos: Position, capturesOnly = false): number[] {
    const out: number[] = [];
    if (this.variant.replaceMoves && this.variant.replaceMoves(pos, out, capturesOnly)) return out;
    const b = pos.board;
    const us = pos.turn;
    const usBit = colorBit(us);
    const themBit = colorBit(other(us));

    for (let sq = 0; sq < 64; sq++) {
      const p = b[sq];
      if (!(p & usBit)) continue;
      const t = p & TYPE_MASK;

      if (this.isPawn[t]) {
        this.pawnMoves(pos, sq, t, out, capturesOnly);
        continue;
      }

      const leaps = this.leapTo[us][t][sq];
      for (let i = 0; i < leaps.length; i++) {
        const to = leaps[i];
        const q = b[to];
        if (q === 0) {
          if (!capturesOnly) out.push(encodeMove(sq, to));
        } else if (q & themBit) out.push(encodeMove(sq, to, 0, FLAG_CAPTURE));
      }
      if (!capturesOnly) {
        const quiet = this.leapMoveTo[us][t][sq];
        for (let i = 0; i < quiet.length; i++) if (b[quiet[i]] === 0) out.push(encodeMove(sq, quiet[i]));
      }
      const caps = this.leapCapTo[us][t][sq];
      for (let i = 0; i < caps.length; i++) if (b[caps[i]] & themBit) out.push(encodeMove(sq, caps[i], 0, FLAG_CAPTURE));

      const rays = this.rays[us][t][sq];
      for (let d = 0; d < rays.length; d++) {
        const r = rays[d];
        for (let i = 0; i < r.length; i++) {
          const to = r[i];
          const q = b[to];
          if (q === 0) {
            if (!capturesOnly) out.push(encodeMove(sq, to));
            continue;
          }
          if (q & themBit) out.push(encodeMove(sq, to, 0, FLAG_CAPTURE));
          break;
        }
      }

      if (this.castling && !capturesOnly && this.isRoyal[t] && t === KING) this.castlingMoves(pos, sq, out);
    }

    if (this.drops && pos.hands && !capturesOnly) this.dropMoves(pos, out);
    if (this.variant.generate) this.variant.generate(pos, out, capturesOnly);
    return out;
  }

  private pawnMoves(pos: Position, sq: number, t: number, out: number[], capturesOnly: boolean) {
    const b = pos.board;
    const us = pos.turn;
    const themBit = colorBit(other(us));
    const dir = us === WHITE ? 8 : -8;
    const r = sq >> 3;
    const one = sq + dir;
    if (one < 0 || one > 63) return;
    const promoting = r === (us === WHITE ? 6 : 1);
    if (b[one] === 0) {
      if (promoting) this.pushPromotions(out, sq, one, 0);
      else if (!capturesOnly) {
        out.push(encodeMove(sq, one));
        if (this.doublePawnStep && r === (us === WHITE ? 1 : 6) && b[one + dir] === 0) {
          out.push(encodeMove(sq, one + dir, 0, FLAG_DOUBLE));
        }
      }
    }
    const caps = this.pawnCapTo[us][t][sq];
    for (let i = 0; i < caps.length; i++) {
      const to = caps[i];
      const q = b[to];
      const promo = (to >> 3) === (us === WHITE ? 7 : 0);
      if (q & themBit) {
        if (promo) this.pushPromotions(out, sq, to, FLAG_CAPTURE);
        else out.push(encodeMove(sq, to, 0, FLAG_CAPTURE));
      } else if (to === pos.ep && q === 0 && this.enPassant && this.isPawn[b[to - dir] & TYPE_MASK] && b[to - dir] & themBit) {
        out.push(encodeMove(sq, to, 0, FLAG_EP | FLAG_CAPTURE));
      }
    }
  }

  private pushPromotions(out: number[], from: number, to: number, flags: number) {
    for (const p of this.promotions) out.push(encodeMove(from, to, p, flags));
  }

  private castlingMoves(pos: Position, sq: number, out: number[]) {
    const b = pos.board;
    const us = pos.turn;
    const them = other(us);
    const home = us === WHITE ? 4 : 60;
    if (sq !== home) return;
    const rook = makePiece(us, ROOK);
    const kRight = us === WHITE ? CASTLE_WK : CASTLE_BK;
    const qRight = us === WHITE ? CASTLE_WQ : CASTLE_BQ;
    if (!(pos.castling & (kRight | qRight))) return;
    const safe = (s: number) => !this.checks || !this.isAttacked(b, s, them);
    if (!safe(sq)) return;
    if (pos.castling & kRight && b[sq + 1] === 0 && b[sq + 2] === 0 && b[sq + 3] === rook && safe(sq + 1)) {
      out.push(encodeMove(sq, sq + 2, 0, FLAG_CASTLE));
    }
    if (
      pos.castling & qRight &&
      b[sq - 1] === 0 &&
      b[sq - 2] === 0 &&
      b[sq - 3] === 0 &&
      b[sq - 4] === rook &&
      safe(sq - 1)
    ) {
      out.push(encodeMove(sq, sq - 2, 0, FLAG_CASTLE));
    }
  }

  private dropMoves(pos: Position, out: number[]) {
    const hands = pos.hands!;
    const base = pos.turn * 32;
    for (const t of this.types) {
      if (hands[base + t] <= 0) continue;
      for (let sq = 0; sq < 64; sq++) {
        if (pos.board[sq] !== 0) continue;
        if (this.isPawn[t] && (sq < 8 || sq >= 56)) continue;
        out.push(encodeMove(t, sq, 0, FLAG_DROP));
      }
    }
  }

  /**
   * Plays a pseudo-legal move, including variant effects. Returns the new position, or null
   * if the move is illegal. Pass `events` to collect what happened (for the UI).
   */
  apply(pos: Position, m: number, events: MoveEvent[] | null = null): Position | null {
    const us = pos.turn;
    const them = other(us);
    const b = pos.board.slice();
    const flags = moveFlags(m);
    const to = moveTo(m);
    const promo = movePromo(m);
    let castling = pos.castling;
    let ep = -1;
    let halfmove = pos.halfmove + 1;
    const royals: [number, number] = [pos.royals[0], pos.royals[1]];
    const hands = pos.hands ? pos.hands.slice() : null;
    const ext = pos.ext ? pos.ext.slice() : null;
    let piece: number;
    let captured = 0;
    let capturedSq = -1;

    if (flags & FLAG_ACTION) {
      // A variant action: the variant's afterMove does whatever it means.
      piece = 0;
    } else if (flags & FLAG_DROP && this.defs[moveFrom(m)]?.neutral) {
      // Placing a neutral piece (the goose, a wall…): no hand needed, variants generate these.
      piece = moveFrom(m);
      if (b[to] !== 0) return null;
      b[to] = piece;
      events?.push({ t: 'add', sq: to, piece, cause: 'drop' });
    } else if (flags & FLAG_DROP) {
      const t = moveFrom(m);
      piece = makePiece(us, t);
      if (!hands || hands[us * 32 + t] <= 0 || b[to] !== 0) return null;
      hands[us * 32 + t]--;
      b[to] = piece;
      if (this.isRoyal[t]) royals[us] = to;
      if (this.isPawn[t]) halfmove = 0;
      events?.push({ t: 'add', sq: to, piece, cause: 'drop' });
    } else {
      const from = moveFrom(m);
      piece = b[from];
      const t = piece & TYPE_MASK;
      captured = b[to];
      capturedSq = to;
      b[from] = 0;
      if (flags & FLAG_EP) {
        capturedSq = to + (us === WHITE ? -8 : 8);
        captured = b[capturedSq];
        b[capturedSq] = 0;
      }
      const placed = promo ? promo | (piece & COLOR_MASK) : piece;
      b[to] = placed;
      events?.push({ t: 'move', from, to, piece });
      if (captured) {
        halfmove = 0;
        events?.push({ t: 'remove', sq: capturedSq, piece: captured, cause: 'capture' });
        const cc = pieceColor(captured);
        if (cc !== -1 && royals[cc] === capturedSq) royals[cc] = -1;
        if (this.captureToHand && hands && cc !== -1) {
          const ct = captured & TYPE_MASK;
          if (!this.isRoyal[ct]) hands[us * 32 + ct]++;
        }
      }
      if (promo) events?.push({ t: 'add', sq: to, piece: placed, cause: 'promotion' });
      if (this.isPawn[t]) halfmove = 0;
      if (this.isRoyal[t]) {
        royals[us] = to;
        if (flags & FLAG_CASTLE) {
          const rf = to > from ? from + 3 : from - 4;
          const rt = to > from ? from + 1 : from - 1;
          b[rt] = b[rf];
          b[rf] = 0;
          events?.push({ t: 'move', from: rf, to: rt, piece: b[rt] });
        }
      }
      castling &= CASTLE_MASK[from] & CASTLE_MASK[to];
      if (flags & FLAG_DOUBLE && this.enPassant) {
        // Only record en passant when an enemy pawn could actually take, so that
        // otherwise-identical positions compare equal for repetition.
        // Any pawn type (a zombie pawn too).
        const themBit = colorBit(them);
        const enemyPawn = (p: number) => (p & themBit) !== 0 && this.isPawn[p & TYPE_MASK];
        const f = to & 7;
        if ((f > 0 && enemyPawn(b[to - 1])) || (f < 7 && enemyPawn(b[to + 1]))) ep = (from + to) >> 1;
      }
    }

    const next: Position = {
      board: b,
      turn: them,
      castling,
      ep,
      halfmove,
      fullmove: pos.fullmove,
      royals,
      hands,
      ext,
    };

    const v = this.variant;
    let ctx: MoveContext | null = null;
    if (v.afterMove || v.nextTurn || v.isLegal) {
      ctx = new MoveContext(this, pos, next, m, us, them, piece, captured, capturedSq, events);
      if (v.afterMove) v.afterMove(ctx);
      if (v.nextTurn) next.turn = v.nextTurn(ctx);
    }
    // A side moving again can't use its own en passant square. Actions set ep themselves (a
    // duel's winner gets back the square saved before the duel).
    if (next.turn === us && !(flags & FLAG_ACTION)) next.ep = -1;
    if (us === BLACK && next.turn === WHITE) next.fullmove++;

    if (this.checks) {
      const k = next.royals[us];
      if (this.hasRoyal && k < 0) return null;
      const won = this.royalKillWins && this.hasRoyal && next.royals[them] < 0;
      if (k >= 0 && !won && this.isAttacked(next.board, k, them)) return null;
    }
    if (v.isLegal && !v.isLegal(ctx!)) return null;
    // Keep en passant only if the capture is actually legal (a pinned pawn can't take), so
    // that positions compare equal for repetition.
    if (next.ep >= 0 && !this.epCaptureLegal(next)) next.ep = -1;
    return next;
  }

  /** Whether the side to move has a legal en passant capture onto `pos.ep`. */
  private epCaptureLegal(pos: Position): boolean {
    const ep = pos.ep;
    // Something standing on the square (a goose) blocks the capture.
    if (pos.board[ep] !== 0) return false;
    const us = pos.turn;
    const pushed = ep + (us === WHITE ? -8 : 8);
    const usBit = colorBit(us);
    for (const df of [-1, 1]) {
      const f = (pushed & 7) + df;
      if (f < 0 || f > 7) continue;
      const from = pushed + df;
      const p = pos.board[from];
      if (!(p & usBit) || !this.isPawn[p & TYPE_MASK]) continue;
      if (this.apply(pos, encodeMove(from, ep, 0, FLAG_EP | FLAG_CAPTURE))) return true;
    }
    return false;
  }

  /**
   * Could `color` still deliver mate by any sequence of moves? Used to turn a timeout into a
   * draw. Only variants with the standard insufficient-material rule ever answer false.
   */
  canMate(pos: Position, color: Color): boolean {
    if (!this.deadMaterialRule(pos)) return true;
    let heavy = 0;
    let minors = 0;
    let theirs = 0;
    for (let sq = 0; sq < 64; sq++) {
      const p = pos.board[sq];
      const c = pieceColor(p);
      if (c === -1) continue;
      // A wounded knight is still a knight here.
      const t = baseType(p & TYPE_MASK);
      if (t === KING) continue;
      if (c !== color) theirs++;
      else if (t === KNIGHT || t === BISHOP) minors++;
      else heavy++;
    }
    if (heavy > 0 || minors >= 2) return true;
    // A lone minor piece can only mate if the other side has pieces to box its own king in.
    return minors === 1 && theirs > 0;
  }

  /** Whether the standard insufficient-material rule applies in this position. */
  deadMaterialRule(pos: Position): boolean {
    const rule = this.variant.insufficientMaterial ?? false;
    return typeof rule === 'function' ? rule(pos) : rule;
  }

  legalMoves(pos: Position): number[] {
    if (this.terminal(pos)) return [];
    const legal: number[] = [];
    for (const m of this.pseudoMoves(pos)) if (this.apply(pos, m)) legal.push(m);
    return this.variant.filterLegal ? this.variant.filterLegal(pos, legal) : legal;
  }

  hasLegalMove(pos: Position): boolean {
    if (this.terminal(pos)) return false;
    for (const m of this.pseudoMoves(pos)) if (this.apply(pos, m)) return true;
    return false;
  }

  /** Game-ending conditions that don't need move generation. Null if none applies. */
  terminal(pos: Position): GameStatus | null {
    const v = this.variant;
    if (v.terminal) {
      const s = v.terminal(pos);
      if (s) return s;
    }
    if (this.hasRoyal && (v.loseWithoutRoyal ?? true)) {
      if (pos.royals[WHITE] < 0) return win(BLACK, 'king-captured');
      if (pos.royals[BLACK] < 0) return win(WHITE, 'king-captured');
    }
    return null;
  }

  /** Result when the side to move has no legal moves. */
  noMovesStatus(pos: Position): GameStatus {
    if (this.variant.noMoves) return this.variant.noMoves(pos);
    return this.inCheck(pos) ? win(other(pos.turn), 'checkmate') : draw('stalemate');
  }

  /** Status of a position on its own (repetition needs the game history; see Game). */
  status(pos: Position): GameStatus {
    const t = this.terminal(pos);
    if (t) return t;
    if (!this.hasLegalMove(pos)) return this.noMovesStatus(pos);
    if (this.deadMaterialRule(pos) && insufficientMaterial(pos.board)) return draw('insufficient');
    if ((this.variant.fiftyMoveRule ?? true) && pos.halfmove >= 100) return draw('fifty-move');
    return ONGOING;
  }

  perft(pos: Position, depth: number): number {
    if (depth === 0) return 1;
    if (this.variant.filterLegal) {
      const legal = this.legalMoves(pos);
      if (depth === 1) return legal.length;
      let n = 0;
      for (const m of legal) n += this.perft(this.apply(pos, m)!, depth - 1);
      return n;
    }
    if (this.terminal(pos)) return 0;
    let n = 0;
    for (const m of this.pseudoMoves(pos)) {
      const next = this.apply(pos, m);
      if (!next) continue;
      n += depth === 1 ? 1 : this.perft(next, depth - 1);
    }
    return n;
  }
}

/** Standard dead-position test: bare kings, a single minor piece, or same-coloured bishops only. */
export function insufficientMaterial(board: Int8Array): boolean {
  let knights = 0;
  let light = 0;
  let dark = 0;
  for (let sq = 0; sq < 64; sq++) {
    const p = board[sq];
    if (p === 0) continue;
    if (!(p & (WHITE_BIT | BLACK_BIT))) continue;
    const t = baseType(p & TYPE_MASK);
    if (t === KING) continue;
    if (t === KNIGHT) knights++;
    else if (t === BISHOP) {
      if (isLightSquare(sq)) light++;
      else dark++;
    } else return false;
  }
  const minors = knights + light + dark;
  if (minors <= 1) return true;
  return knights === 0 && (light === 0 || dark === 0);
}

const cache = new WeakMap<Variant, Rules>();

export function rulesFor(variant: Variant): Rules {
  let r = cache.get(variant);
  if (!r) {
    r = new Rules(variant);
    cache.set(variant, r);
  }
  return r;
}

